// ═══════════════════════════════════════════════════════════
// Tasks Tab — create / assign / complete workflow tasks
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  IconPlus, IconCheck, IconClock, IconAlertCircle,
  IconLoader2, IconChevronDown, IconGitBranch, IconTrash,
} from '@tabler/icons-react';
import {
  listTasksForRequest, createTask, updateTaskStatus, deleteTask,
  type Task, type TaskType, type TaskStatus,
} from '../../../../api/tasks';
import { DropdownMenu } from '../../../../components/ui/DropdownMenu';
import {
  listWorkflows, applyWorkflowToRequest,
  type Workflow,
} from '../../../../api/workflows';

const JURISDICTION_ID = import.meta.env.VITE_JURISDICTION_ID ?? '1';

interface Props {
  requestId:     number;
  actorEmail?:   string;
  deadlineDate?: string;
}

const TASK_TYPES: { value: TaskType; label: string }[] = [
  { value: 'acknowledge',  label: 'Acknowledgment' },
  { value: 'gather',       label: 'Records Gathering' },
  { value: 'review',       label: 'Review' },
  { value: 'redact',       label: 'Redaction' },
  { value: 'legalReview',  label: 'Legal Review' },
  { value: 'approve',      label: 'Approval' },
  { value: 'invoice',      label: 'Invoice / Fees' },
  { value: 'release',      label: 'Release' },
  { value: 'other',        label: 'Other' },
];

const STATUS_STYLE: Record<TaskStatus, { bg: string; text: string; label: string }> = {
  open:       { bg: 'var(--cds-gray-100)', text: 'var(--cds-gray-600)', label: 'Open' },
  inProgress: { bg: 'var(--color-primary-100)', text: 'var(--color-primary-700)', label: 'In Progress' },
  complete:   { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)', label: 'Complete' },
  blocked:    { bg: '#FBEFED', text: 'var(--cds-red-700)', label: 'Blocked' },
};

export default function TasksTab({ requestId, actorEmail, deadlineDate }: Props) {
  const [tasks, setTasks]         = useState<Task[]>([]);
  const [loading, setLoading]     = useState(true);
  const [adding, setAdding]       = useState(false);
  const [saving, setSaving]       = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [wfOpen, setWfOpen]       = useState(false);
  const [applying, setApplying]   = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const [form, setForm] = useState({
    type:    'gather' as TaskType,
    title:   '',
    description: '',
    dueDate: '',
    isBlocking: false,
  });

  useEffect(() => {
    setLoading(true);
    listTasksForRequest(requestId)
      .then(setTasks)
      .finally(() => setLoading(false));
    listWorkflows(JURISDICTION_ID).then(setWorkflows);
  }, [requestId]);

  async function handleApplyWorkflow(wf: Workflow) {
    setWfOpen(false);
    setApplyError(null);
    setApplying(true);
    try {
      await applyWorkflowToRequest(wf.recordId, requestId, actorEmail ?? '', { deadlineDate });
      const updated = await listTasksForRequest(requestId);
      setTasks(updated);
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Failed to apply workflow.');
    } finally {
      setApplying(false);
    }
  }

  async function handleAdd() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const task = await createTask({
        requestId,
        type:        form.type,
        title:       form.title.trim(),
        description: form.description,
        dueDate:     form.dueDate || undefined,
        isBlocking:  form.isBlocking,
      }, actorEmail);
      setTasks(t => [...t, task]);
      setForm({ type: 'gather', title: '', description: '', dueDate: '', isBlocking: false });
      setAdding(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleStatus(task: Task, status: TaskStatus) {
    const updated = await updateTaskStatus(task.recordId, status, actorEmail, requestId);
    setTasks(ts => ts.map(t => t.recordId === task.recordId ? updated : t));
  }

  async function handleDelete(task: Task) {
    if (!confirm(`Delete task "${task.title}"?`)) return;
    await deleteTask(task.recordId);
    setTasks(ts => ts.filter(t => t.recordId !== task.recordId));
  }

  const open     = tasks.filter(t => t.status !== 'complete');
  const complete = tasks.filter(t => t.status === 'complete');

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
        <IconLoader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-700)' }}>
          {open.length} open · {complete.length} complete
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Apply Workflow */}
          {workflows.filter(w => w.isActive).length > 0 && (
            <DropdownMenu
              open={wfOpen}
              onClose={() => setWfOpen(false)}
              align="right"
              minWidth={220}
              trigger={
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ gap: 4 }}
                  onClick={() => setWfOpen(o => !o)}
                  disabled={applying}
                >
                  {applying
                    ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    : <><IconGitBranch size={12} /> Apply Workflow</>
                  }
                </button>
              }
            >
              <div style={{ padding: '6px 12px 4px', fontSize: 12, fontWeight: 700, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Choose Workflow
              </div>
              {workflows.filter(w => w.isActive).map(wf => (
                <button
                  key={wf.recordId}
                  onClick={() => handleApplyWorkflow(wf)}
                  style={{
                    display: 'block', width: '100%', padding: '8px 14px',
                    textAlign: 'left', fontSize: 12, border: 'none',
                    background: 'white', cursor: 'pointer',
                    color: 'var(--color-neutral-800)',
                    borderTop: '1px solid var(--color-neutral-100)',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{wf.name}</div>
                  {wf.description && <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 1 }}>{wf.description}</div>}
                </button>
              ))}
            </DropdownMenu>
          )}
          <button
            className="btn btn-secondary btn-sm"
            style={{ gap: 4 }}
            onClick={() => setAdding(true)}
          >
            <IconPlus size={13} /> Add Task
          </button>
        </div>
      </div>

      {applyError && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#FBEFED', borderRadius: 4, fontSize: 12, color: 'var(--cds-red-700)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconAlertCircle size={13} /> {applyError}
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div style={{
          background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)',
          borderRadius: 4, padding: '14px 16px', marginBottom: 14,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8 }}>
            <select
              className="form-input"
              style={{ fontSize: 12 }}
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value as TaskType }))}
            >
              {TASK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              className="form-input"
              style={{ fontSize: 14 }}
              placeholder="Task title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              autoFocus
            />
          </div>
          <textarea
            className="form-input"
            style={{ fontSize: 12, resize: 'vertical', minHeight: 60 }}
            placeholder="Description (optional)"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Due date</label>
              <input
                type="date"
                className="form-input"
                style={{ fontSize: 12 }}
                value={form.dueDate}
                onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 16 }}>
              <input
                type="checkbox"
                checked={form.isBlocking}
                onChange={e => setForm(f => ({ ...f, isBlocking: e.target.checked }))}
              />
              Blocking
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            <button
              className="btn btn-primary btn-sm"
              disabled={!form.title.trim() || saving}
              onClick={handleAdd}
            >
              {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Save Task'}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {tasks.length === 0 && !adding && (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
          No tasks yet. Add tasks to track work on this request.
        </div>
      )}

      {/* Open tasks */}
      {open.map(task => <TaskRow key={task.recordId} task={task} onStatus={handleStatus} onDelete={handleDelete} />)}

      {/* Completed tasks (collapsible) */}
      {complete.length > 0 && (
        <CompletedSection tasks={complete} onStatus={handleStatus} onDelete={handleDelete} />
      )}
    </div>
  );
}

function TaskRow({ task, onStatus, onDelete }: { task: Task; onStatus: (t: Task, s: TaskStatus) => void; onDelete: (t: Task) => void }) {
  const s = STATUS_STYLE[task.status];
  const typeLabel = TASK_TYPES.find(t => t.value === task.type)?.label ?? task.type;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 16,
      padding: '12px 14px',
      background: 'white',
      border: '1px solid var(--color-neutral-200)',
      borderRadius: 4,
      marginBottom: 8,
    }}>
      {/* Complete toggle */}
      <button
        onClick={() => onStatus(task, task.status === 'complete' ? 'open' : 'complete')}
        style={{
          width: 20, height: 20, borderRadius: '50%',
          border: `2px solid ${task.status === 'complete' ? 'var(--cds-green-700)' : '#D1D5DB'}`,
          background: task.status === 'complete' ? 'var(--cds-green-700)' : 'white',
          cursor: 'pointer', flexShrink: 0, marginTop: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {task.status === 'complete' && <IconCheck size={10} style={{ color: 'white' }} />}
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 14, fontWeight: 500,
            color: task.status === 'complete' ? 'var(--color-neutral-400)' : 'var(--color-neutral-800)',
            textDecoration: task.status === 'complete' ? 'line-through' : 'none',
          }}>
            {task.title}
          </span>
          {task.isBlocking && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 600, color: 'var(--cds-red-700)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <IconAlertCircle size={10} /> Blocking
            </span>
          )}
          <span style={{ padding: '1px 7px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: s.bg, color: s.text }}>
            {s.label}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 3, display: 'flex', gap: 16 }}>
          <span>{typeLabel}</span>
          {task.dueDate && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <IconClock size={10} /> Due {new Date(task.dueDate).toLocaleDateString()}
            </span>
          )}
          {task.description && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{task.description}</span>}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 4 }}>
      <DropdownMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        align="right"
        minWidth={130}
        trigger={
          <button
            className="btn btn-secondary btn-sm"
            style={{ fontSize: 12, gap: 3, padding: '3px 8px' }}
            onClick={() => setMenuOpen(m => !m)}
          >
            Status <IconChevronDown size={10} />
          </button>
        }
      >
        {(Object.keys(STATUS_STYLE) as TaskStatus[]).map(s => (
          <button
            key={s}
            onClick={() => { onStatus(task, s); setMenuOpen(false); }}
            style={{
              display: 'block', width: '100%', padding: '8px 14px',
              textAlign: 'left', fontSize: 12, border: 'none',
              background: task.status === s ? 'var(--color-neutral-50)' : 'white',
              cursor: 'pointer', fontWeight: task.status === s ? 600 : 400,
              color: 'var(--color-neutral-800)',
              borderTop: '1px solid var(--color-neutral-100)',
            }}
          >
            {STATUS_STYLE[s].label}
          </button>
        ))}
      </DropdownMenu>
      <button
        className="btn btn-secondary btn-sm"
        style={{ padding: '3px 7px', color: 'var(--cds-red-700)' }}
        onClick={() => onDelete(task)}
        title="Delete task"
      >
        <IconTrash size={11} />
      </button>
      </div>
    </div>
  );
}

function CompletedSection({ tasks, onStatus, onDelete }: { tasks: Task[]; onStatus: (t: Task, s: TaskStatus) => void; onDelete: (t: Task) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          border: 'none', background: 'none', cursor: 'pointer', padding: '6px 0',
          fontSize: 12, color: 'var(--color-neutral-500)', fontWeight: 500,
        }}
      >
        <IconChevronDown size={13} style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
        {tasks.length} completed task{tasks.length !== 1 ? 's' : ''}
      </button>
      {open && tasks.map(task => <TaskRow key={task.recordId} task={task} onStatus={onStatus} onDelete={onDelete} />)}
    </div>
  );
}
